import { GridRowsProp } from "@mui/x-data-grid";
import { motion } from "motion/react";
import { useCallback, useMemo } from "react";

import StatusFilterItem from "@/components/table/StatusFilterItem";
import { hotelClinicStatusItems } from "@/constants/hotelClinicStatuses";
import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import { HotelClinicTaskResolved } from "@/pages/HotelPlanning/clinic-arrival/types";
import {
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksStatusFilter,
} from "@/stores/use-clinic-arrival-tasks-grid";

export function ClinicArrivalTasksStatusFilter({ allRows }: { allRows: GridRowsProp }) {
    const statusFilter = useClinicArrivalTasksStatusFilter();
    const { updateStatusFilter } = useClinicArrivalTasksGridActions();

    const activeStyle = useMemo(() => {
        for (const [index, item] of hotelClinicStatusItems.entries()) {
            if (item.status === statusFilter) {
                return { translateX: `${index * 100}%`, backgroundColor: item.hover };
            }
        }
        return false;
    }, [statusFilter]);

    const vals = useMemo(
        () =>
            allRows.reduce(
                (prev, curr) => {
                    const value = (curr as HotelClinicTaskResolved).status;
                    if (value) prev[value] = (prev[value] || 0) + 1;
                    return prev;
                },
                {} as Record<HotelClinicStatus, number>
            ),
        [allRows]
    );

    const handleUpdate = useCallback(
        (status: HotelClinicStatus) => {
            updateStatusFilter(statusFilter === status ? null : status);
        },
        [statusFilter, updateStatusFilter]
    );

    const filterElements = useMemo(
        () =>
            hotelClinicStatusItems.map((item) => (
                <StatusFilterItem<HotelClinicStatus>
                    key={item.status}
                    {...item}
                    value={item.status}
                    tooltip={item.tooltip}
                    active={item.status === statusFilter}
                    count={vals[item.status] || 0}
                    handleUpdate={handleUpdate}
                />
            )),
        [handleUpdate, vals, statusFilter]
    );

    return (
        <div className="relative py-1 grid grid-cols-3 bg-gray-100/80 divide-x divide-gray-300 rounded">
            {filterElements}
            {activeStyle && (
                <motion.div
                    layoutId="clinic-arrival-tasks-status-filter"
                    id="clinic-arrival-tasks-status-filter"
                    animate={activeStyle}
                    transition={{ type: "tween" }}
                    className="rounded-none absolute !self-center !h-[calc(100%-8px)] !w-1/3"
                />
            )}
        </div>
    );
}
